import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle2, ScanBarcode, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface BarcodeFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired when a scanner sends Enter, the camera reads a code, or the user presses Enter. */
  onScanned?: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
}

type Detector = { detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue: string }>> };
type DetectorCtor = new (opts?: { formats?: string[] }) => Detector;

function getDetectorCtor(): DetectorCtor | null {
  const w = window as unknown as { BarcodeDetector?: DetectorCtor };
  return typeof w.BarcodeDetector === 'function' ? w.BarcodeDetector : null;
}

/**
 * A serial-number input built for three ways in:
 *  1. a USB / Bluetooth keyboard-wedge scanner typing into the focused field
 *     and sending Enter,
 *  2. the tablet camera, where the browser has `BarcodeDetector` (Chrome on
 *     Android; falls back silently elsewhere),
 *  3. typing it.
 */
export function BarcodeField({
  value,
  onChange,
  onScanned,
  placeholder = 'Scan or type the serial number',
  autoFocus,
  disabled,
  className,
  inputClassName,
}: BarcodeFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const canUseCamera = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!getDetectorCtor();

  useEffect(() => {
    if (autoFocus && !cameraOpen) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
  }, [autoFocus, cameraOpen]);

  const commit = useCallback(
    (raw: string) => {
      const v = raw.trim();
      onChange(v);
      if (v) onScanned?.(v);
    },
    [onChange, onScanned],
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-stretch gap-2">
        <div className="relative min-w-0 flex-1">
          <ScanBarcode className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            inputMode="text"
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit(e.currentTarget.value);
              }
            }}
            className={cn('h-14 pl-11 pr-11 font-mono text-lg tracking-wide', inputClassName)}
            aria-label="Serial number"
          />
          {value ? (
            <button
              type="button"
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
              }}
              disabled={disabled}
              aria-label="Clear serial number"
              className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        {canUseCamera && (
          <Button
            type="button"
            variant={cameraOpen ? 'secondary' : 'outline'}
            size="lg"
            className="h-14 shrink-0"
            disabled={disabled}
            onClick={() => setCameraOpen((o) => !o)}
          >
            <Camera className="h-5 w-5" />
            {cameraOpen ? 'Close camera' : 'Camera'}
          </Button>
        )}
      </div>
      {cameraOpen && (
        <CameraScanner
          onDetected={(code) => {
            setCameraOpen(false);
            commit(code);
          }}
          onClose={() => setCameraOpen(false)}
        />
      )}
      {value && !cameraOpen && (
        <p className="flex items-center gap-1.5 text-sm text-bright-moss-dark">
          <CheckCircle2 className="h-4 w-4" />
          Serial captured. Press Enter or scan again to replace it.
        </p>
      )}
    </div>
  );
}

function CameraScanner({ onDetected, onClose }: { onDetected: (code: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const Ctor = getDetectorCtor();
    if (!Ctor) {
      setError('This browser cannot read barcodes from the camera. Use a scanner or type the number.');
      return;
    }
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let cancelled = false;
    const detector = new Ctor({
      formats: ['code_128', 'code_39', 'code_93', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code', 'data_matrix', 'itf', 'codabar'],
    });

    const tick = async () => {
      if (cancelled) return;
      const video = videoRef.current;
      if (video && video.readyState >= 2) {
        try {
          const codes = await detector.detect(video);
          const hit = codes.find((c) => c.rawValue && c.rawValue.trim().length > 0);
          if (hit && !cancelled) {
            try {
              navigator.vibrate?.(40);
            } catch {
              /* not supported */
            }
            onDetected(hit.rawValue.trim());
            return;
          }
        } catch {
          /* a frame failed to decode; try the next one */
        }
      }
      timer = window.setTimeout(() => void tick(), 180);
    };

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        const video = videoRef.current;
        if (video) {
          video.srcObject = s;
          void video.play().catch(() => undefined);
        }
        void tick();
      })
      .catch(() => {
        if (!cancelled) setError('Could not open the camera. Check the browser permission, or use a scanner.');
      });

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onDetected]);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-humus-dark">
      <video ref={videoRef} playsInline muted className="aspect-video w-full object-cover" />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-[36%] w-[72%] rounded-xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
      </div>
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 text-sm text-white">
        <span>{error ?? 'Hold the barcode inside the frame.'}</span>
        <button type="button" onClick={onClose} className="rounded-full bg-white/15 px-3 py-1 hover:bg-white/25">
          Cancel
        </button>
      </div>
    </div>
  );
}
